import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowLeft, FaChevronLeft, FaChevronRight,
  FaRegClipboard, FaBookOpen, FaCheck,
  FaFacebookF, FaInstagram, FaYoutube
} from 'react-icons/fa';
import { getProducts, getArticles, getAds, getLinks } from '../services/api';
import ProductCard from '../components/ProductCard';
import ArticleCard from '../components/ArticleCard';
import { getImageUrl } from '../config';
import '../styles/Home.css';

const prefersCalm = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* The six questions a mother actually arrives with. Each one is just a
   friendlier door into an article category that already exists. */
const NEEDS = [
  { key: 'pregnancy', title: 'أنا حامل', sub: 'كل ما يخص الحمل شهر بشهر', icon: '🤰' },
  { key: 'childcare', title: 'عندي بيبي', sub: 'العناية، الرضاعة، النوم والتطور', icon: '👶' },
  { key: 'recipes', title: 'واش نطيب؟', sub: 'وصفات سهلة وسريعة للعائلة', icon: '🍲' },
  { key: 'home', title: 'نحب نظّم داري', sub: 'روتين، ترتيب، وأفكار بسيطة', icon: '🏠' },
  { key: 'education', title: 'ولادي', sub: 'تربية، أنشطة، ونمو الأطفال', icon: '👧' },
  { key: 'health', title: 'صحتي وصحتهم', sub: 'نصائح صحية للأم والطفل', icon: '💪' }
];

/* What the organiser promises, in four short beats. */
const ORGANIZER_POINTS = [
  { icon: '📔', label: 'منظّم شامل', sub: 'كل شي في كتاب واحد' },
  { icon: '🗓️', label: 'جداول جاهزة', sub: 'سهل تعمّريهم' },
  { icon: '✂️', label: 'أدوات عملية', sub: 'جاهزة للطباعة' },
  { icon: '⬇️', label: 'متوفر الآن', sub: 'حمّليه بضغطة' }
];

/* ── A warm little scene, drawn in SVG — no photography, no image files. ── */
const HeroArt = () => (
  <svg className="hero-art" viewBox="0 0 440 360" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="ma-halo" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FBE6D8" />
        <stop offset="1" stopColor="#F5D3CF" />
      </linearGradient>
      <linearGradient id="ma-dress" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFFDFA" />
        <stop offset="1" stopColor="#F1DED0" />
      </linearGradient>
      <path
        id="ma-heart"
        d="M0 -3 c 4 -9 17 -6 17 4 c 0 9 -11 15 -17 20 c -6 -5 -17 -11 -17 -20 c 0 -10 13 -13 17 -4 Z"
      />
    </defs>

    <circle cx="228" cy="180" r="152" fill="url(#ma-halo)" />
    <circle cx="352" cy="74" r="30" fill="#F9E3CB" opacity="0.85" />

    {/* a little plant on the floor */}
    <path d="M62 356 h44 l-6 -30 h-32 Z" fill="#E9BE93" />
    <path d="M84 326 V282" stroke="#B0BFA5" strokeWidth="5" strokeLinecap="round" />
    <path d="M84 312 C 62 304 56 284 58 268 C 78 273 86 292 84 312 Z" fill="#DCE4D5" />
    <path d="M84 298 C 106 290 114 270 112 254 C 92 259 82 278 84 298 Z" fill="#C6D3BC" />

    {/* the mother */}
    <path d="M172 356 C 172 268 194 232 240 232 C 286 232 308 268 308 356 Z" fill="url(#ma-dress)" />
    <path d="M240 122 C 284 122 302 154 302 192 C 302 224 290 246 268 254 L 212 254 C 190 246 178 224 178 192 C 178 154 196 122 240 122 Z" fill="#DE9DA1" />
    <ellipse cx="240" cy="188" rx="31" ry="35" fill="#F3D2B8" />
    <path d="M240 122 C 284 122 302 154 302 192 C 302 199 301 206 299 212 C 289 194 277 168 240 168 C 203 168 191 194 181 212 C 179 206 178 199 178 192 C 178 154 196 122 240 122 Z" fill="#CB8288" />
    <circle cx="228" cy="190" r="3.4" fill="#5A3E44" />
    <circle cx="253" cy="190" r="3.4" fill="#5A3E44" />
    <path d="M230 204 C 235 211 246 211 251 204" stroke="#5A3E44" strokeWidth="3" fill="none" strokeLinecap="round" />
    <circle cx="219" cy="200" r="6" fill="#F2B9B4" opacity="0.7" />
    <circle cx="262" cy="200" r="6" fill="#F2B9B4" opacity="0.7" />

    {/* the daughter */}
    <path d="M112 356 C 112 300 128 276 160 276 C 192 276 208 300 208 356 Z" fill="#EFC3BE" />
    <ellipse cx="160" cy="246" rx="25" ry="28" fill="#F6DAC2" />
    <path d="M134 248 C 132 220 144 208 160 208 C 176 208 188 220 186 248 C 182 233 174 227 160 227 C 146 227 138 233 134 248 Z" fill="#4A3138" />
    <circle cx="190" cy="250" r="11" fill="#4A3138" />
    <circle cx="151" cy="248" r="3.2" fill="#4A3138" />
    <circle cx="170" cy="248" r="3.2" fill="#4A3138" />
    <path d="M152 260 C 156 265 165 265 169 260" stroke="#4A3138" strokeWidth="2.6" fill="none" strokeLinecap="round" />
    <circle cx="143" cy="256" r="5" fill="#F2B9B4" opacity="0.7" />
    <circle cx="178" cy="256" r="5" fill="#F2B9B4" opacity="0.7" />

    {/* her arm, around her girl */}
    <path d="M206 278 C 190 266 174 264 160 270" stroke="#F1DED0" strokeWidth="17" fill="none" strokeLinecap="round" />

    <g fill="#EAB6B8">
      <use href="#ma-heart" transform="translate(330 128) scale(1.1)" />
      <use href="#ma-heart" transform="translate(372 176) scale(0.72)" opacity="0.8" />
      <use href="#ma-heart" transform="translate(96 138) scale(0.85)" opacity="0.7" />
    </g>
  </svg>
);

/* Some older links point at images that no longer exist; fall back to a glyph. */
const ToolIcon = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="tool-icon" aria-hidden="true">📄</span>;
  return (
    <span className="tool-icon" aria-hidden="true">
      <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
};

/* ── Reusable horizontal carousel ── */
const HorizontalCarousel = ({ children, label, autoScrollInterval = 6000 }) => {
  const trackRef = useRef(null);
  const timerRef = useRef(null);

  const getScrollAmount = () => {
    if (!trackRef.current) return 440;
    const first = trackRef.current.querySelector('.carousel-item');
    if (!first) return 440;
    return (first.offsetWidth + 22) * 2;
  };

  const scroll = useCallback((dir) => {
    if (!trackRef.current) return;
    const amount = getScrollAmount();
    trackRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  const autoScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft >= maxScroll - 10) el.scrollTo({ left: 0, behavior: 'smooth' });
    else scroll('right');
  }, [scroll]);

  useEffect(() => {
    if (prefersCalm()) return undefined;
    timerRef.current = setInterval(autoScroll, autoScrollInterval);
    return () => clearInterval(timerRef.current);
  }, [autoScroll, autoScrollInterval]);

  const pause = () => clearInterval(timerRef.current);
  const resume = () => {
    if (prefersCalm()) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(autoScroll, autoScrollInterval);
  };

  return (
    <div
      className="carousel-wrapper"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocusCapture={pause}
      onBlurCapture={resume}
    >
      <button
        type="button"
        className="carousel-arrow carousel-arrow-right"
        onClick={() => scroll('right')}
        aria-label={'التالي' + (label ? ' في ' + label : '')}
      >
        <FaChevronRight aria-hidden="true" />
      </button>
      <button
        type="button"
        className="carousel-arrow carousel-arrow-left"
        onClick={() => scroll('left')}
        aria-label={'السابق' + (label ? ' في ' + label : '')}
      >
        <FaChevronLeft aria-hidden="true" />
      </button>
      <ul className="carousel-track" ref={trackRef} tabIndex={0} aria-label={label}>
        {React.Children.map(children, (child, i) => (
          <li className="carousel-item" key={i}>{child}</li>
        ))}
      </ul>
    </div>
  );
};

/* ── Cache helpers ── */
const CACHE_KEY = 'home_cache';
const getCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; } };
const setCache = (key, data) => {
  try { const c = getCache(); c[key] = data; localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) { /* quota */ }
};

const excerpt = (text, max) => {
  const body = (text || '').trim();
  if (body.length <= max) return body;
  return body.substring(0, max).trim() + '…';
};

const Home = () => {
  const cached = getCache();
  const [featuredProducts, setFeaturedProducts] = useState(cached.featuredProducts || []);
  const [picks, setPicks] = useState(cached.picks || []);
  const [recipe, setRecipe] = useState(cached.recipe || null);
  const [tools, setTools] = useState(cached.tools || []);
  const [heroAds, setHeroAds] = useState(cached.heroAds || []);
  const [sponsorAds, setSponsorAds] = useState(cached.sponsorAds || []);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [failedPhoto, setFailedPhoto] = useState(null);
  const [productsLoaded, setProductsLoaded] = useState(!!cached.featuredProducts);
  const [articlesLoaded, setArticlesLoaded] = useState(!!cached.picks);

  useEffect(() => {
    getAds({ position: 'hero', active: true }).then((res) => {
      const d = Array.isArray(res.data) ? res.data : [];
      setHeroAds(d); setCache('heroAds', d);
    }).catch(() => {});

    getProducts({ featured: true }).then((res) => {
      const d = Array.isArray(res.data) ? res.data.slice(0, 12) : [];
      setFeaturedProducts(d); setProductsLoaded(true); setCache('featuredProducts', d);
    }).catch(() => setProductsLoaded(true));

    getArticles().then((res) => {
      const d = Array.isArray(res.data) ? res.data : [];
      const featured = d.filter((a) => a.featured);
      /* four picks: the featured ones first, topped up with the newest */
      const chosen = featured.concat(d.filter((a) => !a.featured)).slice(0, 4);
      const todaysRecipe = d.find((a) => a.category === 'recipes') || null;

      setPicks(chosen); setCache('picks', chosen);
      setRecipe(todaysRecipe); setCache('recipe', todaysRecipe);
      setArticlesLoaded(true);
    }).catch(() => setArticlesLoaded(true));

    getLinks({ active: true }).then((res) => {
      const d = Array.isArray(res.data) ? res.data.slice(0, 5) : [];
      setTools(d); setCache('tools', d);
    }).catch(() => {});

    getAds({ position: 'sponsor', active: true }).then((res) => {
      const d = Array.isArray(res.data) ? res.data : [];
      setSponsorAds(d); setCache('sponsorAds', d);
    }).catch(() => {});
  }, []);

  /* slide 0 is always the welcome; every hero ad becomes a slide after it */
  const slideCount = heroAds.length + 1;

  useEffect(() => {
    if (slideCount < 2 || prefersCalm()) return undefined;
    const timer = setInterval(() => setCurrentSlide((p) => (p + 1) % slideCount), 7000);
    return () => clearInterval(timer);
  }, [slideCount]);

  const index = Math.min(currentSlide, slideCount - 1);
  const ad = index === 0 ? null : heroAds[index - 1];
  /* the welcome slide is filled by public/hero.jpg; an ad slide by its own image */
  const heroPhoto = ad ? getImageUrl(ad.image) : '/hero.jpg';
  const showPhoto = !!heroPhoto && failedPhoto !== heroPhoto;

  return (
    <div className="home-page">
      {/* ══════════ Hero ══════════ */}
      <section className="hero" aria-labelledby="hero-title">
        <div className="container">
          <div className={'hero-panel' + (showPhoto ? ' has-photo' : '')}>
            {showPhoto ? (
              <>
                <img
                  className="hero-bleed"
                  src={heroPhoto}
                  alt=""
                  onError={() => setFailedPhoto(heroPhoto)}
                />
                <span className="hero-scrim" aria-hidden="true" />
              </>
            ) : (
              <div className="hero-glow" aria-hidden="true" />
            )}

            {!ad ? (
              <motion.div
                className="hero-copy"
                key="welcome"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 id="hero-title">
                  ماما جزائرية <span className="hero-heart" aria-hidden="true">💗</span>
                </h1>
                <p className="hero-lead">
                  كل ما تحتاجه الأم الجزائرية
                  <br />
                  لتنظيم حياتها وعائلتها <span aria-hidden="true">💗</span>
                </p>
                <div className="hero-actions">
                  <Link to="/links" className="hero-btn primary">
                    <FaRegClipboard aria-hidden="true" /> أدوات الأم
                  </Link>
                  <Link to="/articles" className="hero-btn secondary">
                    <FaBookOpen aria-hidden="true" /> اكتشفي نصائحنا
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                className="hero-copy"
                key={ad._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <span className="sticker">جديد</span>
                <h2 className="hero-ad-title">{ad.title}</h2>
                {ad.description && <p className="hero-lead">{excerpt(ad.description, 140)}</p>}
                {ad.link && (
                  <div className="hero-actions">
                    <Link to={ad.link} className="hero-btn primary">
                      اكتشفي المزيد <FaArrowLeft aria-hidden="true" />
                    </Link>
                  </div>
                )}
              </motion.div>
            )}

            {!showPhoto && (
              <div className="hero-visual">
                <HeroArt />
              </div>
            )}

            <aside className="hero-note">
              <span className="hero-note-title">كلمة للأم</span>
              <p>
                ماشي لازم تكوني أم مثالية...
                <br />
                يكفي تكوني أم تحاول كل يوم <span aria-hidden="true">💗</span>
              </p>
            </aside>
          </div>

          {slideCount > 1 && (
            <div className="slider-dots">
              {Array.from({ length: slideCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={'dot' + (i === index ? ' active' : '')}
                  onClick={() => setCurrentSlide(i)}
                  aria-label={'الشريحة ' + (i + 1)}
                  aria-current={i === index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══════════ What do you need today? ══════════ */}
      <section className="needs-section" aria-labelledby="needs-title">
        <div className="container">
          <h2 className="band-title" id="needs-title">
            <span aria-hidden="true">💗</span> واش تحتاجي اليوم يا ماما؟ <span aria-hidden="true">💗</span>
          </h2>

          <ul className="needs-grid">
            {NEEDS.map((need, i) => (
              <li key={need.key}>
                <Link to={'/articles?category=' + need.key} className={'need-card tone-' + i}>
                  <span className="need-icon" aria-hidden="true">{need.icon}</span>
                  <span className="need-title">{need.title}</span>
                  <span className="need-sub">{need.sub}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══════════ This week's picks ══════════ */}
      <section className="picks-section" aria-labelledby="picks-title">
        <div className="container">
          <h2 className="band-title" id="picks-title">
            <span aria-hidden="true">🌸</span> مختاراتنا لهذا الأسبوع
          </h2>

          {!articlesLoaded ? (
            <div className="picks-grid" aria-hidden="true">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-card skeleton-article">
                  <div className="skeleton-img" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line short" />
                </div>
              ))}
            </div>
          ) : picks.length > 0 ? (
            <div className="picks-grid">
              {picks.map((a) => <ArticleCard key={a._id} article={a} />)}
            </div>
          ) : null}

          <div className="band-foot">
            <Link to="/articles" className="pill-link">
              كل المقالات <FaArrowLeft aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════ Everyday tools — the download library ══════════ */}
      {tools.length > 0 && (
        <section className="tools-section" aria-labelledby="tools-title">
          <div className="container">
            <h2 className="band-title" id="tools-title">
              <span aria-hidden="true">☀️</span> أدوات تساعدك كل يوم
            </h2>

            <ul className="tools-grid">
              {tools.map((tool, i) => (
                <li key={tool._id}>
                  <Link to="/links" className={'tool-card tone-' + (i % 6)}>
                    <ToolIcon src={getImageUrl(tool.image)} />
                    <span className="tool-title">{tool.titleAr || tool.title}</span>
                    {(tool.descriptionAr || tool.description) && (
                      <span className="tool-sub">{excerpt(tool.descriptionAr || tool.description, 42)}</span>
                    )}
                    <span className="tool-cta">استخدمي الآن <FaArrowLeft aria-hidden="true" /></span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="band-foot">
              <Link to="/links" className="pill-link">
                شوفي جميع الأدوات <FaArrowLeft aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ══════════ What are we cooking today? ══════════ */}
      {recipe && (
        <section className="kitchen-section" aria-labelledby="kitchen-title">
          <div className="container">
            <h2 className="band-title" id="kitchen-title">
              <span aria-hidden="true">👩‍🍳</span> واش نطيب اليوم؟
            </h2>

            <div className="kitchen-layout">
              <article className="recipe-panel">
                <div className="recipe-thumb">
                  {getImageUrl(recipe.image)
                    ? <img src={getImageUrl(recipe.image)} alt="" loading="lazy" />
                    : <span className="recipe-placeholder" aria-hidden="true">🍲</span>}
                </div>
                <div className="recipe-body">
                  <span className="sticker">اقتراح اليوم <span aria-hidden="true">💗</span></span>
                  <h3>{recipe.titleAr || recipe.title}</h3>
                  <p>{excerpt(recipe.contentAr || recipe.content, 150)}</p>
                  <Link to={'/articles/' + recipe._id} className="hero-btn primary recipe-cta">
                    شوفي وصفة اليوم <FaArrowLeft aria-hidden="true" />
                  </Link>
                </div>
              </article>

              <aside className="pantry-card">
                <span className="pantry-icon" aria-hidden="true">🥕</span>
                <h3>عندك مكونات في دارك؟</h3>
                <p>
                  قريباً... نساعدوك تلقاي وصفات
                  <br />
                  بالمكونات اللي عندك.
                </p>
                <Link to="/articles?category=recipes" className="pill-link">
                  كل الوصفات <FaArrowLeft aria-hidden="true" />
                </Link>
              </aside>
            </div>
          </div>
        </section>
      )}

      {/* ══════════ Featured products ══════════ */}
      <section className="featured-products-section" aria-labelledby="prod-title">
        <div className="container">
          <div className="section-header band-header">
            <h2 className="band-title inline" id="prod-title">
              <span aria-hidden="true">🛍️</span> منتجات مميزة
            </h2>
            <Link to="/products" className="view-all-link">عرض الكل <FaArrowLeft aria-hidden="true" /></Link>
          </div>

          {!productsLoaded ? (
            <div className="skeleton-carousel" aria-hidden="true">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-img" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line short" />
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <HorizontalCarousel label="منتجات مميزة">
              {featuredProducts.map((p) => <ProductCard key={p._id} product={p} />)}
            </HorizontalCarousel>
          ) : null}
        </div>
      </section>

      {/* ══════════ The organiser + newsletter ══════════ */}
      <section className="organizer-section" aria-labelledby="organizer-title">
        <div className="container">
          <motion.div
            className="organizer-panel"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
          >
            <div className="organizer-copy">
              <h2 id="organizer-title">خذي وقتك... ونظّمي حياتك خطوة بخطوة</h2>
              <p>
                من تنظيم دارك، إلى وجبات عائلتك، إلى ذكرياتك الصغيرة —
                نرافقوك في يومك بكل بساطة.
              </p>

              <div className="organizer-social">
                <span>تابعينا</span>
                <a href="https://www.facebook.com/MamanAlgerienne/" target="_blank" rel="noopener noreferrer" aria-label="فيسبوك"><FaFacebookF aria-hidden="true" /></a>
                <a href="https://www.instagram.com/mamanalgerienne/" target="_blank" rel="noopener noreferrer" aria-label="إنستغرام"><FaInstagram aria-hidden="true" /></a>
                <a href="https://www.youtube.com/c/mamanalgerienne" target="_blank" rel="noopener noreferrer" aria-label="يوتيوب"><FaYoutube aria-hidden="true" /></a>
              </div>
            </div>

            <div className="organizer-card">
              <span className="organizer-badge" aria-hidden="true">📔</span>
              <h3>منظّم الأم الجزائرية</h3>
              <ul className="organizer-points">
                {ORGANIZER_POINTS.map((point) => (
                  <li key={point.label}>
                    <span className="point-icon" aria-hidden="true">{point.icon}</span>
                    <span className="point-text">
                      <strong>{point.label}</strong>
                      <em>{point.sub}</em>
                    </span>
                  </li>
                ))}
              </ul>
              <Link to="/links" className="pill-link solid">
                <FaCheck aria-hidden="true" /> اكتشفي المنظّم
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════ Sponsors ══════════ */}
      {sponsorAds.length > 0 && (
        <section className="sponsor-section" aria-labelledby="sponsor-title">
          <div className="container">
            <h2 className="band-title" id="sponsor-title">
              <span aria-hidden="true">🤝</span> شركاؤنا
            </h2>
            <div className="sponsor-grid">
              {sponsorAds.map((adItem) => (
                <a
                  key={adItem._id}
                  href={adItem.link || '#'}
                  target={adItem.link ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="sponsor-card"
                >
                  <img src={getImageUrl(adItem.image)} alt={adItem.title} loading="lazy" />
                  <span className="sponsor-overlay"><span>{adItem.title}</span></span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
